/**
 * Shared UI translations for /web and /mobile (i18next resources).
 * French is the reference; English is complete from day one; more languages (including
 * right-to-left ones) will be added from the back office. TCF exam content always stays in French.
 */

import accessEn from "../locales/access.en.json";
import academyHistoryEn from "../locales/academyHistory.en.json";
import academyHistoryFr from "../locales/academyHistory.fr.json";
import freeMockEn from "../locales/freeMock.en.json";
import freeMockFr from "../locales/freeMock.fr.json";
import accessFr from "../locales/access.fr.json";
import reportsEn from "../locales/activityReports.en.json";
import reportsFr from "../locales/activityReports.fr.json";
import salesEn from "../locales/academySales.en.json";
import salesFr from "../locales/academySales.fr.json";
import callsEn from "../locales/calls.en.json";
import callsFr from "../locales/calls.fr.json";
import examFrameEn from "../locales/examFrame.en.json";
import examFrameFr from "../locales/examFrame.fr.json";
import coachingEn from "../locales/coaching.en.json";
import coachingFr from "../locales/coaching.fr.json";
import conformityEn from "../locales/conformity.en.json";
import conformityFr from "../locales/conformity.fr.json";
import baseEn from "../locales/en.json";
import baseFr from "../locales/fr.json";
import goetheEn from "../locales/goethe.en.json";
import goetheFr from "../locales/goethe.fr.json";
import licensingEn from "../locales/licensing.en.json";
import licensingFr from "../locales/licensing.fr.json";
import onboardingEn from "../locales/onboarding.en.json";
import onboardingFr from "../locales/onboarding.fr.json";
import promisesEn from "../locales/promises.en.json";
import promisesFr from "../locales/promises.fr.json";
import rankingEn from "../locales/ranking.en.json";
import rankingFr from "../locales/ranking.fr.json";
import supportEn from "../locales/support.en.json";
import supportFr from "../locales/support.fr.json";
import saasEn from "../locales/saas.en.json";
import saasFr from "../locales/saas.fr.json";

/** Namespaced catalogues kept in their own files (fewer conflicts between teams): the Academy
 * online sale (D-110) lives under « academySales »,
 * German (Goethe-Zertifikat, D-118) under « goethe », the students and team positions (D-120) under
 * « access », the trainer reviews and oral credits (D-124) under « promises », the support centre
 * (D-129) under « support », the mock oral with a trainer, the 7-day Pass, the free start and the
 * learners' reviews (D-142) under « coaching », the new agency setup wizard (D-148) under « onboarding », the
 * agency subscription (D-147) under « saas », the WhatsApp calls (D-151) under « calls », the self-hosted licence banner
 * (D-149) under « licensing », the Academy activity reports e-mailed to the administrators (D-161) under
 * « activityReports », the free mock exam (D-172) under « freeMock », the exam conformity
 * fixes of 2026 (D-163) under « conformity », the learner's own rank explained (D-165) under « ranking »,
 * the exam screens' frame (D-173) under « examFrame », the learner's history (D-174) under
 * « academyHistory ». */
const fr = { ...baseFr, academySales: salesFr, goethe: goetheFr, access: accessFr, promises: promisesFr, support: supportFr, coaching: coachingFr, onboarding: onboardingFr, saas: saasFr, calls: callsFr, licensing: licensingFr, activityReports: reportsFr, freeMock: freeMockFr, conformity: conformityFr, ranking: rankingFr, examFrame: examFrameFr, academyHistory: academyHistoryFr };
const en = { ...baseEn, academySales: salesEn, goethe: goetheEn, access: accessEn, promises: promisesEn, support: supportEn, coaching: coachingEn, onboarding: onboardingEn, saas: saasEn, calls: callsEn, licensing: licensingEn, activityReports: reportsEn, freeMock: freeMockEn, conformity: conformityEn, ranking: rankingEn, examFrame: examFrameEn, academyHistory: academyHistoryEn };

export const REFERENCE_LANGUAGE = "fr";
export const SUPPORTED_LANGUAGES = ["fr", "en"] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];
export const RTL_LANGUAGES: readonly string[] = ["ar", "fa", "he", "ur"];
export const LANGUAGE_STORAGE_KEY = "agencyos.lang";

export const resources = { fr: { translation: fr }, en: { translation: en } } as const;
export type TranslationTree = typeof fr;

export interface Tree {
  [key: string]: string | Tree;
}

export function isSupported(language: string): language is Language {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(language);
}

export function isRtl(language: string): boolean {
  return RTL_LANGUAGES.includes(language.split("-")[0] ?? language);
}

/** Flattened keys, e.g. « auth.sendCode ». */
export function flattenKeys(tree: Tree, prefix = ""): string[] {
  return Object.entries(tree).flatMap(([key, value]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    return typeof value === "string" ? [path] : flattenKeys(value, path);
  });
}

export function missingKeys(reference: Tree, candidate: Tree): string[] {
  const present = new Set(flattenKeys(candidate));
  return flattenKeys(reference).filter((key) => !present.has(key));
}

export function emptyKeys(tree: Tree, prefix = ""): string[] {
  return Object.entries(tree).flatMap(([key, value]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if (typeof value === "string") return value.trim() === "" ? [path] : [];
    return emptyKeys(value, path);
  });
}

/** Placeholders like {{name}} must be identical in every language. */
export function placeholderMismatches(reference: Tree, candidate: Tree): string[] {
  const lookup = (tree: Tree, key: string): string =>
    String(key.split(".").reduce<unknown>((node, part) => (node as Tree)[part], tree) ?? "");
  const vars = (text: string): string => (text.match(/\{\{\s*\w+\s*\}\}/g) ?? []).sort().join(",");
  return flattenKeys(reference).filter(
    (key) => flattenKeys(candidate).includes(key) && vars(lookup(reference, key)) !== vars(lookup(candidate, key)),
  );
}
